import React, { useMemo } from 'react';
import cx from 'classnames';
import pluralize from 'pluralize';

export enum EmptyStateLayoutType {
  full = 'full',
  block = 'block',
}

export interface EmptyTitleProps {
  noun: string;
  tagNames?: string;
}

export function EmptyTitle({ noun, tagNames }: EmptyTitleProps) {
  const plural = pluralize(noun);

  const title =
    tagNames != null
      ? `Woops! There are currently no ${plural} under ${tagNames}`
      : `You don’t have any ${plural} yet`;

  return <>{title}</>;
}

export interface EmptyBodyProps {
  className?: string;
  noun: string;
  layout: EmptyStateLayoutType;
  variant: 'default' | 'tagged';
  defaultBody: React.ReactNode;
  onYourLibraryClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export function EmptyBody({
  className,
  variant = 'default',
  noun,
  layout,
  onYourLibraryClick,
  defaultBody,
}: EmptyBodyProps) {
  const bodyClassname =
    layout == EmptyStateLayoutType.full ? 'mb-4 text-lg' : 'text-base px-2';

  const body =
    variant === 'default' ? (
      defaultBody
    ) : (
      <>
        But don&apos;t fret! Just jump back into{' '}
        <a className='cursor-pointer' onClick={onYourLibraryClick}>
          your library
        </a>{' '}
        and start adding this tag to the relevant {pluralize(noun)}.
      </>
    );

  return <p className={cx(className, bodyClassname)}>{body}</p>;
}

export interface EmptyStateProps {
  layout?: EmptyStateLayoutType;
  imageUrl?: string;
  className?: string;
  title: React.ReactNode | (() => React.ReactNode);
  description?: string;
  body?: React.ReactNode | (() => React.ReactNode);
  actions?: React.ReactNode;
}

const EmptyState = ({
  layout = EmptyStateLayoutType.full,
  className,
  title,
  description,
  body,
  imageUrl,
  actions,
}: EmptyStateProps) => {
  const [
    wrapperClassName,
    contentWrapperClassName,
    imageClassName,
    descriptionClassName,
    contentClassName,
  ] = useMemo(() => {
    return layout == EmptyStateLayoutType.full
      ? [
          'w-full px-[5%] mt-5',
          'flex',
          'mr-8 max-w-full md:max-w-[40%]',
          'text-lg',
          'my-auto md:max-w-[50%]',
        ]
      : [
          'w-full',
          'text-center',
          'w-auto max-h-[150px] mb-5',
          'text-base px-2',
          'm-auto',
        ];
  }, [layout]);

  const Title = typeof title === 'function' ? title : () => <>{title}</>;

  const Body = typeof body === 'function' ? body : () => <>{body}</>;

  return (
    <div className={cx(wrapperClassName, className)}>
      <div className={contentWrapperClassName}>
        {imageUrl && <img className={imageClassName} src={imageUrl} />}
        <div className={contentClassName}>
          {layout === EmptyStateLayoutType.full ? (
            <h1 className='mb-4 mt-0'>
              <Title />
            </h1>
          ) : (
            <h2 className='mb-4 mt-0 text-black font-bold'>{title}</h2>
          )}

          {description && (
            <p className={cx('mb-4', descriptionClassName)}>{description}</p>
          )}

          <Body />

          {actions && actions}
        </div>
      </div>
    </div>
  );
};

export default EmptyState;
